<template>
  <tabs :key="'procedure_tabs_' + procedureId" entity-type="procedure" :entity-id="procedureId" :tabs="tabs" />
</template>

<script>
import { mapState } from 'vuex';

import Tabs from '@/components/tabs/Tabs';
import { EntityType } from '@/enums/entityType.enum';
import { Errors } from '@/enums/errors.enum';
import { PackStatus } from '@/enums/packStatus.enum';
import { ProcedureCodes } from '@/enums/procedureCodes.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { TableName } from '@/enums/tableName';
import { koMixin } from '@/mixins/ko';
import { notificationsMixin } from '@/mixins/notifications';
import { getProcedureDetailsData } from '@/utils/procedure';

// @vuese
// Szczegóły dokumentu
export default {
  components: { Tabs },
  mixins: [koMixin, notificationsMixin],
  computed: {
    procedureId() {
      return this.$route.params.procedureId.toString();
    },
    ...mapState({
      settingTabs: state => state.procedure.tabs,
      decisionReportColumnSetting: state => state.setting.settings[PublicSettings.DECISION_REPORT_COLUMNS],
      reportColumnsSetting: state => state.setting.settings[PublicSettings.REPORT_COLUMNS],
      reportGroupsSetting: state => state.setting.settings[PublicSettings.REPORT_GROUPS],
      reportFinancialColumnsSetting: state => state.setting.settings[PublicSettings.FINANCIAL_REPORT_COLUMNS],
      reportStatisticsSetting: state => state.setting.settings[PublicSettings.STATISTICS_COLUMNS],
      procedure: state => state.procedure.data,
      packsEnabled: state => state.setting.settings[PublicSettings.PACK_ENABLED]?.value === 'true',
      viewIds: state => state.setting.settings[PublicSettings.PROCEDURE_VIEW_IDS],
      structureNodes: state => state.auth.nodes?.map(node => node.id),
      hideDocumentation: state => state.setting.settings[PublicSettings.HIDE_PROCEDURE_DOCUMENTATION]?.value === 'true',
      hideDocumentationJO: state =>
        state.setting.settings[PublicSettings.HIDE_PROCEDURE_DOCUMENTATION_FOR_JO]?.value === 'true',
      hideGenerateJO: state => state.setting.settings[PublicSettings.HIDE_PROCEDURE_GENERATE_FOR_JO]?.value === 'true',
      hidePayments: state => state.setting.settings[PublicSettings.HIDE_PROCEDURE_PAYMENTS]?.value === 'true'
    }),
    tabs() {
      return [...this.baseTabs(), ...this.settingTabs];
    }
  },
  methods: {
    // @vuese
    // Podstawowe zakładki procedury
    baseTabs() {
      const { parentId, parentProcedureId } = this.$route.query;

      const emptyQuery = {
        tabId: undefined,
        statusId: undefined,
        search: undefined,
        notPreviousStatus: undefined,
        previousStatuses: undefined
      };

      if (parentId && parentProcedureId) {
        return [
          {
            title: 'Informacje o wniosku',
            route: {
              name: 'documentView',
              params: { procedureId: parentProcedureId, documentId: parentId },
              query: { parentId: undefined, parentProcedureId: undefined, ...emptyQuery }
            }
          }
        ];
      }

      const tabs = [
        {
          title: 'Informacje o procedurze',
          route: {
            name: 'procedureView',
            query: emptyQuery
          },
          permissions: ['PROCEDURE.GET']
        }
      ];

      if (!this.hidePayments) {
        tabs.push({
          title: this.koConfig.paymentPacksTabName || 'Płatności',
          route: {
            name: 'procedurePaymentPacks',
            query: emptyQuery
          },
          permissions: ['PAYMENT.LIST', 'PAYMENT.LIST_SPECIAL'],
          entityId: this.structureNodes,
          entityType: EntityType.STRUCTURE_NODE,
          joinType: 'OR'
        });
      }

      if (
        (!this.hideDocumentation || this.structureNodes?.length) &&
        (!this.hideDocumentationJO || !this.structureNodes?.length)
      ) {
        tabs.push({
          title: 'Dokumentacja',
          route: {
            name: 'procedureDocumentation',
            query: emptyQuery
          },
          permissions: ['ATTACHMENT.DOCUMENTATION', 'ATTACHMENT.DOCUMENTATION_VERIFICATION'],
          entityType: TableName.procedure,
          entityId: this.$route.params.procedureId.toString(),
          joinType: 'OR'
        });
      }

      if (this.viewIds && (!this.hideGenerateJO || !this.structureNodes?.length)) {
        tabs.push({
          title: 'Generowanie',
          route: {
            name: 'procedureGenerate',
            query: emptyQuery
          },
          permissions: ['ATTACHMENT.DOCUMENTATION'],
          entityType: TableName.procedure,
          entityId: this.$route.params.procedureId.toString(),
          joinType: 'OR'
        });
      }

      const reportsTab = {
        title: 'Raportowanie',
        children: []
      };

      if (this.decisionReportColumnSetting) {
        reportsTab.children.push({
          title: 'Decyzje',
          route: {
            name: 'procedureReportDecisions',
            query: { ...emptyQuery, viewId: undefined }
          },
          permissions: ['DECISION.REPORT'],
          entityType: TableName.procedure,
          entityId: this.$route.params.procedureId.toString()
        });
      }

      if (this.reportColumnsSetting) {
        reportsTab.children.push({
          title: 'Ewidencja',
          route: {
            name: 'procedureReportList',
            query: emptyQuery
          },
          permissions: ['PROCEDURE.REPORT']
        });
      }

      if (this.reportGroupsSetting) {
        reportsTab.children.push({
          title: 'Raporty',
          route: {
            name: 'procedureReportGroups',
            query: emptyQuery
          },
          permissions: ['PROCEDURE.REPORT']
        });
      }

      if (this.reportStatisticsSetting) {
        reportsTab.children.push({
          title: 'Statystyki',
          route: {
            name: 'procedureReportStatistics',
            query: emptyQuery
          },
          permissions: ['PROCEDURE.REPORT']
        });
      }

      if (this.reportFinancialColumnsSetting) {
        reportsTab.children.push({
          title: 'Raporty finansowe',
          route: {
            name: 'procedureReportFinancial',
            query: emptyQuery
          },
          permissions: ['PROCEDURE.REPORT_FINANCIAL', 'PROCEDURE.REPORT_FINANCIAL_VERIFICATION'],
          entityType: EntityType.STRUCTURE_NODE,
          entityId: this.structureNodes,
          joinType: 'OR'
        });

        reportsTab.children.push({
          title: 'Plany finansowe',
          route: {
            name: 'procedureFinancialPlan',
            query: emptyQuery
          },
          permissions: ['PROCEDURE.FINANCIAL_PLAN']
        });
      }

      if (this.procedure.code === ProcedureCodes.OW003Scholarship) {
        reportsTab.children.push({
          title: 'Repozytorium faktur',
          route: {
            name: 'procedureInvoices',
            query: { ...emptyQuery, order: undefined }
          },
          permissions: ['PROCEDURE.REPORT_INVOICE_REPOSITORY', 'DOCUMENT.GET:document:-1']
        });
      }

      if (
        this.reportColumnsSetting ||
        this.reportGroupsSetting ||
        this.reportFinancialColumnsSetting ||
        this.reportStatisticsSetting
      ) {
        tabs.push(reportsTab);
      }

      if (this.packsEnabled) {
        tabs.push({
          title: 'Paczki wniosków',
          children: [
            {
              title: 'Wszystkie',
              permissions: ['DOCUMENT_PACK.LIST'],
              entityId: this.structureNodes,
              entityType: TableName.structure_node,
              joinType: 'OR',
              route: {
                name: 'documentPacks',
                query: { ...emptyQuery, status: undefined }
              }
            },
            {
              title: 'Do odebrania',
              permissions: ['DOCUMENT_PACK.LIST'],
              route: {
                name: 'documentPacks',
                query: { ...emptyQuery, status: PackStatus.SENT }
              }
            },
            {
              title: 'Odebrane',
              permissions: ['DOCUMENT_PACK.LIST'],
              route: {
                name: 'documentPacks',
                query: { ...emptyQuery, status: PackStatus.RECEIVED }
              }
            }
          ]
        });
      }

      return tabs;
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (to?.params?.procedureId?.toString() === from?.params?.procedureId?.toString()) {
      return next();
    }
    getProcedureDetailsData(to)
      .then(() => next())
      .catch(() => this.notifyError(Errors.PROCEDURE_CANNOT_GET));
  }
};
</script>
